// =============================================================================
// Origin UI — src/services/evaluationApi.ts
// API service for Evaluation module endpoints (Phase 7).
// =============================================================================

import { api } from './apiService';

// ─── Types ───

export interface RatingScaleOption {
  ratingScaleOptionId: string;
  label: string;
  description: string | null;
  numericValue: number;
  sortOrder: number;
}

export interface RatingScale {
  ratingScaleId: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  options: RatingScaleOption[];
}

export interface TemplateQuestion {
  templateQuestionId: string;
  title: string;
  description: string | null;
  questionType: 'Rating' | 'FreeText';
  ratingScaleId: string | null;
  ratingScaleName: string | null;
  isRequired: boolean;
  hasCommentField: boolean;
  isOverallRating: boolean;
  sortOrder: number;
  ratingOptions: RatingScaleOption[] | null;
}

export interface TemplateSection {
  templateSectionId: string;
  title: string;
  description: string | null;
  respondent: 'Employee' | 'Manager' | 'System';
  sortOrder: number;
  isSystem: boolean;
  questions: TemplateQuestion[];
}

export interface EvaluationCycleListItem {
  evaluationCycleId: string;
  name: string;
  cycleType: string;
  reviewYear: number;
  status: string;
  employeeDeadline: string;
  managerDeadline: string;
  hrFinalDeadline: string;
  isTemplateLocked: boolean;
  isTemplate: boolean;
  evaluationCount: number;
  completedCount: number;
}

export interface EvaluationCycleStatusHistoryEntry {
  evaluationCycleStatusHistoryId: string;
  fromStatus: string;
  toStatus: string;
  comment: string | null;
  changedBy: string;
  changedAt: string;
}

export interface EvaluationCycleDetail extends EvaluationCycleListItem {
  excludedDepartments: string | null;
  clonedFromCycleId: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  templateSections: TemplateSection[];
  statusHistory: EvaluationCycleStatusHistoryEntry[];
}

export interface QuestionWithResponse {
  templateQuestionId: string;
  title: string;
  description: string | null;
  questionType: 'Rating' | 'FreeText';
  isRequired: boolean;
  hasCommentField: boolean;
  isOverallRating: boolean;
  sortOrder: number;
  ratingOptions: RatingScaleOption[] | null;
  evaluationResponseId: string | null;
  ratingScaleOptionId: string | null;
  selectedRatingLabel: string | null;
  selectedRatingValue: number | null;
  comment: string | null;
}

export interface SectionWithResponses {
  templateSectionId: string;
  title: string;
  description: string | null;
  respondent: 'Employee' | 'Manager' | 'System';
  sortOrder: number;
  isSystem: boolean;
  questions: QuestionWithResponse[];
}

export interface EvaluationListItem {
  evaluationId: string;
  evaluationCycleId: string;
  cycleName: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  department: string;
  office: string;
  managerName: string;
  status: string;
  employeeSubmittedAt: string | null;
  managerCompletedAt: string | null;
  employeeAcknowledgedAt: string | null;
  overallRating: string | null;
  cycleType: string;
}

export interface EvaluationDetail extends EvaluationListItem {
  employeeDeadline: string;
  managerDeadline: string;
  cycleType: string;
  cycleStatus: string;
  employeeJobTitle: string;
  reviewYear: number;
  sections: SectionWithResponses[];
}

export interface SaveResponseItem {
  templateQuestionId: string;
  ratingScaleOptionId: string | null;
  comment: string | null;
}

// ─── Report types ───

export interface RatingDistribution {
  ratingLabel: string;
  numericValue: number;
  count: number;
  percentage: number;
}

export interface DepartmentBreakdown {
  department: string;
  employeeCount: number;
  averageRating: number;
  distribution: RatingDistribution[];
}

export interface BiasIndicator {
  dimension: string;
  groupA: string;
  groupB: string;
  groupAAverage: number;
  groupBAverage: number;
  difference: number;
  isFlagged: boolean;
  note: string | null;
}

export interface ManagerCalibration {
  managerName: string;
  managerId: string;
  reviewedCount: number;
  averageRating: number;
  companyAverage: number;
  deviation: number;
  deviationFlag: string;
  distribution: RatingDistribution[];
  biasIndicators: BiasIndicator[] | null;
}

export interface CompletionByDept {
  department: string;
  total: number;
  submitted: number;
  completed: number;
  acknowledged: number;
}

export interface OverdueItem {
  employeeName: string;
  department: string;
  pendingAction: string;
  daysOverdue: number;
}

export interface CompletionTracker {
  totalEligible: number;
  employeeSubmitted: number;
  managerCompleted: number;
  acknowledged: number;
  employeeSubmittedPercent: number;
  managerCompletedPercent: number;
  acknowledgedPercent: number;
  byDepartment: CompletionByDept[];
  overdue: OverdueItem[];
}

export interface PerformanceTrend {
  reviewYear: number;
  cycleName: string;
  averageRating: number;
  distribution: RatingDistribution[];
}

export interface FairnessGroup {
  groupName: string;
  count: number;
  averageRating: number;
  distribution: RatingDistribution[];
  insufficientData: boolean;
}

export interface FairnessHeatmapCell {
  gender: string;
  department: string;
  count: number;
  averageRating: number;
  insufficientData: boolean;
}

export interface FairnessDashboard {
  byGender: FairnessGroup[];
  byTenureBand: FairnessGroup[];
  byOffice: FairnessGroup[];
  intersectionalHeatmap: FairnessHeatmapCell[];
  statisticalFlags: BiasIndicator[];
}

export interface EvaluationEmailTemplate {
  evaluationEmailTemplateId: number;
  subject: string;
  templateBody: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

// ─── API Calls: My Evaluation ───

export interface EvaluationHistoryItem {
  evaluationId: string;
  cycleName: string;
  cycleType: string;
  reviewYear: number;
  status: string;
  overallRating: string | null;
  employeeSubmittedAt: string | null;
  managerCompletedAt: string | null;
  employeeAcknowledgedAt: string | null;
}

export interface CycleEmailConfig {
  cycleEmailConfigId: string;
  emailType: string;
  subject: string;
  content: string;
  triggerDescription: string;
  recipientsDescription: string;
  applicableCycleType: string;
  isEnabled: boolean;
}

export const evaluationApi = {
  // My evaluation
  getEmployeeEvaluationHistory: (employeeId: string) => api.get<EvaluationHistoryItem[]>(`/api/evaluations/employee/${employeeId}/history`),
  getMyEvaluation: () => api.get<EvaluationDetail | { message: string }>('/api/evaluations/mine'),
  getEvaluation: (id: string) => api.get<EvaluationDetail>(`/api/evaluations/${id}`),
  saveResponses: (id: string, responses: SaveResponseItem[]) =>
    api.put(`/api/evaluations/${id}/responses`, { responses }),
  submitEvaluation: (id: string) => api.post(`/api/evaluations/${id}/submit`),
  completeEvaluation: (id: string) => api.post(`/api/evaluations/${id}/complete`),
  acknowledgeEvaluation: (id: string) => api.post(`/api/evaluations/${id}/acknowledge`),
  reopenEvaluation: (id: string) => api.post(`/api/evaluations/${id}/reopen`),

  // Team
  getCompanyEvaluations: () => api.get<EvaluationListItem[]>('/api/evaluations/company'),
  getTeamEvaluations: (cycleId?: string, department?: string, office?: string) => {
    const params = new URLSearchParams();
    if (cycleId) params.append('cycleId', cycleId);
    if (department) params.append('department', department);
    if (office) params.append('office', office);
    return api.get<EvaluationListItem[]>(`/api/evaluations/team?${params}`);
  },

  // Reports
  getRatingDistribution: (cycleId: string, department?: string, office?: string) => {
    const params = new URLSearchParams({ cycleId });
    if (department) params.append('department', department);
    if (office) params.append('office', office);
    return api.get<RatingDistribution[]>(`/api/evaluations/reports/distribution?${params}`);
  },
  getPerformanceTrends: (department?: string, office?: string) => {
    const params = new URLSearchParams();
    if (department) params.append('department', department);
    if (office) params.append('office', office);
    return api.get<PerformanceTrend[]>(`/api/evaluations/reports/trends?${params}`);
  },
  getDepartmentBreakdown: (cycleId: string) =>
    api.get<DepartmentBreakdown[]>(`/api/evaluations/reports/department-breakdown?cycleId=${cycleId}`),
  getManagerCalibration: (cycleId: string) =>
    api.get<ManagerCalibration[]>(`/api/evaluations/reports/manager-calibration?cycleId=${cycleId}`),
  getCompletionTracker: (cycleId: string) =>
    api.get<CompletionTracker>(`/api/evaluations/reports/completion?cycleId=${cycleId}`),
  getDetailReport: (cycleId: string, department?: string, office?: string) => {
    const params = new URLSearchParams({ cycleId });
    if (department) params.append('department', department);
    if (office) params.append('office', office);
    return api.get<EvaluationListItem[]>(`/api/evaluations/reports/detail?${params}`);
  },
  getFairnessDashboard: (cycleId: string, department?: string) => {
    const params = new URLSearchParams({ cycleId });
    if (department) params.append('department', department);
    return api.get<FairnessDashboard>(`/api/evaluations/reports/fairness?${params}`);
  },

  // Admin: Cycles
  getCycles: () => api.get<EvaluationCycleListItem[]>('/api/evaluation-admin/cycles'),
  getCycle: (id: string) => api.get<EvaluationCycleDetail>(`/api/evaluation-admin/cycles/${id}`),
  createCycle: (data: any) => api.post<EvaluationCycleDetail>('/api/evaluation-admin/cycles', data),
  updateCycle: (id: string, data: any) => api.put<EvaluationCycleDetail>(`/api/evaluation-admin/cycles/${id}`, data),
  deleteCycle: (id: string) => api.delete(`/api/evaluation-admin/cycles/${id}`),
  openCycle: (id: string) => api.post<EvaluationCycleDetail>(`/api/evaluation-admin/cycles/${id}/open`),
  moveToInReview: (id: string) => api.post<EvaluationCycleDetail>(`/api/evaluation-admin/cycles/${id}/in-review`),
  closeCycle: (id: string) => api.post<EvaluationCycleDetail>(`/api/evaluation-admin/cycles/${id}/close`),
  reopenCycle: (id: string, comment: string) => api.post<EvaluationCycleDetail>(`/api/evaluation-admin/cycles/${id}/reopen`, { comment }),
  getIncludedEmployees: (cycleId: string) => api.get<{ employeeId: string; employeeName: string; department: string }[]>(`/api/evaluation-admin/cycles/${cycleId}/included-employees`),
  setIncludedEmployees: (cycleId: string, employeeIds: string[]) => api.put(`/api/evaluation-admin/cycles/${cycleId}/included-employees`, { employeeIds }),
  getCycleEmails: (cycleId: string) => api.get<CycleEmailConfig[]>(`/api/evaluation-admin/cycles/${cycleId}/emails`),
  updateCycleEmail: (cycleId: string, configId: string, dto: { subject: string; content: string; isEnabled: boolean }) => api.put<CycleEmailConfig>(`/api/evaluation-admin/cycles/${cycleId}/emails/${configId}`, dto),
  sendReminders: (id: string) => api.post<{ message: string; count: number }>(`/api/evaluation-admin/cycles/${id}/send-reminders`),

  // Admin: Template
  addSection: (cycleId: string, data: any) =>
    api.post(`/api/evaluation-admin/cycles/${cycleId}/template/sections`, data),
  updateSection: (cycleId: string, sectionId: string, data: any) =>
    api.put(`/api/evaluation-admin/cycles/${cycleId}/template/sections/${sectionId}`, data),
  deleteSection: (cycleId: string, sectionId: string) =>
    api.delete(`/api/evaluation-admin/cycles/${cycleId}/template/sections/${sectionId}`),
  reorderSections: (cycleId: string, orderedIds: string[]) =>
    api.put(`/api/evaluation-admin/cycles/${cycleId}/template/sections/reorder`, { orderedIds }),
  addQuestion: (cycleId: string, sectionId: string, data: any) =>
    api.post(`/api/evaluation-admin/cycles/${cycleId}/template/sections/${sectionId}/questions`, data),
  updateQuestion: (cycleId: string, questionId: string, data: any) =>
    api.put(`/api/evaluation-admin/cycles/${cycleId}/template/questions/${questionId}`, data),
  deleteQuestion: (cycleId: string, questionId: string) =>
    api.delete(`/api/evaluation-admin/cycles/${cycleId}/template/questions/${questionId}`),
  reorderQuestions: (cycleId: string, sectionId: string, orderedIds: string[]) =>
    api.put(`/api/evaluation-admin/cycles/${cycleId}/template/sections/${sectionId}/questions/reorder`, { orderedIds }),

  // Admin: Rating Scales
  getRatingScales: () => api.get<RatingScale[]>('/api/evaluation-admin/rating-scales'),
  createRatingScale: (data: any) => api.post<RatingScale>('/api/evaluation-admin/rating-scales', data),
  updateRatingScale: (id: string, data: any) => api.put<RatingScale>(`/api/evaluation-admin/rating-scales/${id}`, data),
  deleteRatingScale: (id: string) => api.delete(`/api/evaluation-admin/rating-scales/${id}`),

  // Admin: Email Template
  getEmailTemplate: () => api.get<EvaluationEmailTemplate>('/api/evaluation-admin/email-template'),
  updateEmailTemplate: (data: { subject: string; templateBody: string }) =>
    api.put<EvaluationEmailTemplate>('/api/evaluation-admin/email-template', data),
  previewEmailTemplate: (cycleId?: string) => {
    const params = cycleId ? `?cycleId=${cycleId}` : '';
    return api.post<{ preview: string }>(`/api/evaluation-admin/email-template/preview${params}`);
  },
};
